import React, { Component } from "react";
import PropTypes from "prop-types";

import TextInput from "components/library/TextInput";
import Button from "components/library/Button";
import FileDropZone from "components/library/FileDropZone";
import { MESSAGES, IMAGE_TYPES } from "config/constants";
import { genKey } from "draft-js";

import styles from "./ImageUploader.css";

const getUrlParam = (str, param) => {
  try {
    const url = new URL(str);
    return url.searchParams.get(param);
  } catch {
    return null;
  }
};

const createCaptionBlock = text => {
  if (!text) return null;

  return {
    entityMap: {},
    blocks: [
      {
        key: genKey(),
        type: "unstyled",
        text,
        depth: 0,
        inlineStyleRanges: [],
        entityRanges: [],
        data: {},
      },
    ],
  };
};

export default class ImageUploader extends Component {
  constructor() {
    super();
    this.upload = this._upload.bind(this);
    this.state = { file: undefined };
  }

  validate(uploadParams) {
    const validationErrs = [];
    const mimetypes = Object.values(IMAGE_TYPES).map(x => x.mimetype);

    if (
      !uploadParams ||
      !((uploadParams instanceof FormData) ^ !!uploadParams.image_by_url)
    ) {
      // One or the other input must exist and have a value (not both)
      validationErrs.push({
        message: "You must either upload an image or specify a URL.",
        type: MESSAGES.failure,
      });
    } else if (
      uploadParams instanceof FormData &&
      !mimetypes.includes(uploadParams.type)
    ) {
      validationErrs.push({
        message: `Please upload one of the following file types: ${mimetypes.join(
          ", "
        )}`,
        type: MESSAGES.failure,
      });
    }

    return validationErrs;
  }

  parseImageUrl(url) {
    const isFromIlloLibrary = Boolean(getUrlParam(url, "axIlloLibrary"));

    if (isFromIlloLibrary) {
      return {
        url: url.split("?")[0],
        source: "Axios Visuals Library",
        caption: getUrlParam(url, "caption"),
        altText: getUrlParam(url, "altText"),
      };
    } else {
      return { url };
    }
  }

  _upload(image, manualTrigger = false) {
    let uploadParams;
    const validationErrs = [];

    if (image instanceof File) {
      const formData = new FormData();
      formData.append("upload", image);
      uploadParams = formData;
      uploadParams.type = image.type;
    } else if (manualTrigger) {
      image.preventDefault();
      const form = image.target.form;
      const urlInput = [...form.elements].find(x => x.name === "url");
      const { url, altText, source, caption } = this.parseImageUrl(
        urlInput.value
      );

      if (altText) {
        this.props.setSuggestedAltText(altText);
      }

      uploadParams = {
        image_by_url: url,
        ...(caption && { caption: createCaptionBlock(caption) }),
        ...(source && { source }),
      };
    } else {
      // I don't think you should be able to get here after the fix in this commit, but just to be safe:
      validationErrs.push({
        message: "Upload not accepted.",
        type: MESSAGES.failure,
      });
    }

    validationErrs.push(...this.validate(uploadParams));

    if (validationErrs.length) {
      return this.props.setMessage(validationErrs[0]);
    } else {
      this.props.setLoadingState(true);
      return this.props
        .onSubmit(uploadParams)
        .then(() => {
          this.props.nextStep();
        })
        .catch(error => {
          this.props.setMessage({ message: error });
          this.props.setLoadingState(false);
        });
    }
  }

  render() {
    return (
      <form className={styles.form} onSubmit={e => e.preventDefault()}>
        <FileDropZone
          onUpload={this.upload}
          helpText={this.props.uploadHelpText(this.props.imageRequirements)}
        />
        <Button
          className={styles.searchButton}
          rel="noreferrer noopener"
          target="_blank"
          to="/illos"
          type="secondary"
        >
          Search Axios Illustrations
        </Button>
        <TextInput
          autoFocus={true}
          data-cy="imageUrlInput"
          fullWidth
          label="Image URL"
          name="url"
          placeholder="Paste an image's URL"
          type="url"
        />
        <div className={styles.buttonGroup}>
          <Button
            onClick={event => this.upload(event, true)}
            type="submit"
            aria-label="Upload the specified image"
            data-cy="submitFeatureImageButton"
          >
            Submit
          </Button>
          <Button
            role="link"
            onClick={() => this.props.history.go(-1)}
            type="cancel"
            aria-label="Cancel image uploading/editing and go back to the text editor"
          >
            Cancel
          </Button>
        </div>
      </form>
    );
  }
}

ImageUploader.propTypes = {
  history: PropTypes.shape({
    go: PropTypes.func.isRequired,
    push: PropTypes.func.isRequired,
  }),
  onSubmit: PropTypes.func.isRequired,
  setMessage: PropTypes.func.isRequired,
  nextStep: PropTypes.func.isRequired,
  setLoadingState: PropTypes.func.isRequired,
  setSuggestedAltText: PropTypes.func.isRequired,
  imageRequirements: PropTypes.shape({
    minWidth: PropTypes.number,
    minHeight: PropTypes.number,
  }),
  uploadHelpText: PropTypes.func,
};
